import React, { useEffect, useRef } from 'react';
import { VideoStreamRenderer } from '@azure/communication-calling';
import mute_incall from './new_assets/in_call_mic_off.svg';
import unmute_incall from './new_assets/in_call_mic_on.svg';
import raise_hand_active from './new_assets/raise_hand.svg';
import './App.css';
import { appendRoleLabel, getParticipantRoleLabel } from './utils/participantRoleUtils';

const RenderDynamicView = ({ remoteVideoStream, item, participantCount, pageIndex, screenShareItem, isScreenSharing = false, micOption, variant = 'grid' }) => {
  const videoContainerRef = useRef(null);
  const rendererRef = useRef(null);
  const viewRef = useRef(null);

  console.group(`🎥 RenderDynamicView - ${item?.displayName || 'Unknown'} (${item?.userId})`);
  console.log('Props:', {
    hasRemoteVideoStream: !!remoteVideoStream,
    streamType: remoteVideoStream?.mediaStreamType,
    isAvailable: remoteVideoStream?.isAvailable,
    item,
    participantCount,
    pageIndex,
    screenShareItem,
    isScreenSharing,
    micOption
  });
  console.groupEnd();

  const getInitials = (str) => {
    try {
      console.log(`📝 Getting initials for: "${str}"`);
      if (!str || typeof str !== 'string') {
        console.warn('⚠️ Invalid string for initials:', str);
        return '';
      }
      if (!str.includes(' ')) {
        const initial = str.slice(0, 1).toUpperCase();
        console.log(`✅ Single word initial: ${initial}`);
        return initial;
      }
      const words = str.split(' ');
      const initials = words.map(w => w[0].toUpperCase()).join('').slice(0, 1);
      console.log(`✅ Initials generated: ${initials}`);
      return initials;
    } catch (error) {
      console.error("❌ Error generating initials:", error);
      return '';
    }
  };

  const getClassName = () => {
    if (variant === 'pinned-main') {
      return 'remoteUser-pinned-main';
    }
    if (variant === 'pinned-side') {
      return 'remoteUser-pinned-side';
    }

    const className = participantCount < 3 ? "remote-one-custom"
      : participantCount === 3 ? "remote-three-custom"
      : participantCount === 4 ? "remote-four-custom"
      : participantCount < 7 ? "remote-morethan-four-custom"
      : "remote-greaterthan-six-custom";
    return className;
  };

  const disposeRenderer = () => {
    try {
      if (viewRef.current) {
        const videoElement = viewRef.current.target?.querySelector('video');
        if (videoElement) {
          videoElement.pause();
        }
        viewRef.current.dispose();
        viewRef.current = null;
      }
      if (rendererRef.current) {
        rendererRef.current.dispose();
        rendererRef.current = null;
      }
    } catch (err) {
      console.error('❌ Dispose error:', err);
    }
  };

  const createUIElements = () => {
    
    if (!videoContainerRef.current) {
      console.warn('⚠️ videoContainerRef.current is null, cannot create UI elements');
      return;
    }

    // Remove all existing UI elements
    const existingElements = {
      shortName: videoContainerRef.current.querySelector('.short-name'),
      nameDiv: videoContainerRef.current.querySelector('.name_div'),
      muteStatus: videoContainerRef.current.querySelectorAll('.mute-status'),
      muteStatusScreenShare: videoContainerRef.current.querySelector('.mute-status-screen-share'),
      riseHand: videoContainerRef.current.querySelector('.risehand-status')
    };

    console.log('Existing elements found:', {
      shortName: !!existingElements.shortName,
      nameDiv: !!existingElements.nameDiv,
      muteStatus: existingElements.muteStatus.length,
      muteStatusScreenShare: !!existingElements.muteStatusScreenShare,
      riseHand: !!existingElements.riseHand
    });

    // Remove elements
    if (existingElements.shortName) existingElements.shortName.remove();
    if (existingElements.nameDiv) existingElements.nameDiv.remove();
    existingElements.muteStatus.forEach(el => el.remove());
    if (existingElements.muteStatusScreenShare) existingElements.muteStatusScreenShare.remove();
    if (existingElements.riseHand) existingElements.riseHand.remove();

    if (item?.userId !== "screen-share") {
      const roleLabel = getParticipantRoleLabel(item);
      const displayNameWithRole = appendRoleLabel(item?.displayName || '', roleLabel);

      // Create short name element
      if (!screenShareItem) {
        const shortNameElement = document.createElement('div');
        shortNameElement.className = 'short-name';
        shortNameElement.innerText = displayNameWithRole;
        videoContainerRef.current.appendChild(shortNameElement);
      }

      // Create name div (initials) when video is off
      if (item?.isVideoOff || !remoteVideoStream?.isAvailable) {
        const initials = getInitials(item?.displayName || '');
        const nameDivElement = document.createElement('div');
        nameDivElement.className = 'name_div';
        nameDivElement.innerText = initials;
        videoContainerRef.current.appendChild(nameDivElement);
      }

      // Create mute status element
      const muteStatusElement = document.createElement('img');
      muteStatusElement.className = isScreenSharing ? "mute-status-screen-share" : 'mute-status';
      muteStatusElement.src = item?.isMute ? mute_incall : unmute_incall;
      muteStatusElement.id = `mute-status-${item?.userId}`;
      muteStatusElement.alt = "Mute Status";
      muteStatusElement.style.backgroundColor = item?.isMute ? '#FF1D1D' : '#16A249';
      
      if (variant === 'pinned-side') {
        muteStatusElement.style.top = '10px';
        muteStatusElement.style.bottom = '';
      } else if (variant === 'pinned-main') {
        muteStatusElement.style.top = '';
        muteStatusElement.style.bottom = '12px';
      } else if (participantCount === 2) {
        muteStatusElement.style.top = '7px';
        muteStatusElement.style.bottom = '';
      } else {
        muteStatusElement.style.bottom = '7px';
        muteStatusElement.style.top = '';
      }
      
      muteStatusElement.style.borderRadius = '50%';
      muteStatusElement.style.padding = '4px';
      if (item?.isMute) { 
        muteStatusElement.classList.add('mute');
      }
      videoContainerRef.current.appendChild(muteStatusElement);

      // Create raise hand element
      const riseHandElement = document.createElement('img');
      riseHandElement.className = 'risehand-status';
      riseHandElement.src = raise_hand_active;
      riseHandElement.id = `risehand-status-${item?.userId}`;
      riseHandElement.alt = "Raise Hand";
      riseHandElement.style.display = item?.isRiseHand ? 'block' : 'none';
      videoContainerRef.current.appendChild(riseHandElement);

      // Apply or remove border based on hand raising status
      if (item?.isRiseHand) {
        videoContainerRef.current.style.border = '2px solid #F25920';
        videoContainerRef.current.style.boxShadow = '0 0 0 2px #F25920';
      } else {
        videoContainerRef.current.style.border = '';
        videoContainerRef.current.style.boxShadow = '';
      }
    }
  };

  const renderStream = async () => {
    
    if (!remoteVideoStream || !videoContainerRef.current) {
      return;
    }

    const isScreenShare = item?.isScreenShare && remoteVideoStream.mediaStreamType === 'ScreenSharing';
    const isAvailable = typeof remoteVideoStream.isAvailable === 'boolean' ? remoteVideoStream.isAvailable : true;

    // Check if video is off
    if (item?.isVideoOff) {
      disposeRenderer();
      createUIElements();
      return;
    }

    if (!isAvailable) {
      disposeRenderer();
      createUIElements();
      return;
    }

    try {
      await new Promise(resolve => setTimeout(resolve, 100));

      if (viewRef.current && videoContainerRef.current.contains(viewRef.current.target)) {
        return;
      }

      disposeRenderer();
      const renderer = new VideoStreamRenderer(remoteVideoStream);
      const view = await renderer.createView();
      while (videoContainerRef.current.firstChild) {
        videoContainerRef.current.removeChild(videoContainerRef.current.firstChild);
      }
      videoContainerRef.current.appendChild(view.target);

      const videoElement = view.target.querySelector('video');
      if (videoElement) {
        console.log('🎬 Styling video element');
        videoElement.style.objectFit = 'contain';
        videoElement.style.width = '100%';
        videoElement.style.height = '100%';
        if (!isScreenShare) {
          videoElement.style.transform = 'scaleX(-1)';
        } else {
          videoElement.style.transform = 'scaleX(1)';
        }
      }

      // Create UI elements after video is rendered
      createUIElements();

      rendererRef.current = renderer;
      viewRef.current = view;
    } catch (err) {
      if (err.name === 'AbortError') {
        return;
      }
      console.error('❌ Failed to render video stream:', err);
      console.log('🎨 Creating UI elements as fallback');
      createUIElements();
    }
  };

  useEffect(() => {

    if (!remoteVideoStream) {
      disposeRenderer();
      createUIElements();
      return;
    }

    const isRemoteStream = remoteVideoStream && remoteVideoStream.kind !== 'LocalVideoStream';

    renderStream();

    const handleAvailabilityChange = () => {
      if (remoteVideoStream) {
        renderStream();
      } else {
        disposeRenderer();
        createUIElements();
      }
    };

    if (isRemoteStream && remoteVideoStream?.on) {
      remoteVideoStream.on('isAvailableChanged', handleAvailabilityChange);
    }

    return () => {
      if (isRemoteStream && remoteVideoStream?.off) {
        remoteVideoStream.off('isAvailableChanged', handleAvailabilityChange);
      }
      disposeRenderer();
    };
  }, [remoteVideoStream, item?.isScreenShare, item?.isVideoOff, item?.isMute, item?.isRiseHand, participantCount, item?.cohost, item?.userType, variant]);

  // Update UI elements when item props change
  useEffect(() => {
    
    if (videoContainerRef.current) {
      console.log('Updating UI elements');
      createUIElements();
    }
  }, [item?.isMute, item?.isRiseHand, item?.isVideoOff, item?.displayName, participantCount, item?.cohost, item?.userType, variant]);

  return (
    <div className={`remoteUser ${getClassName()} ${screenShareItem ? 'remote-custom-screen-share' : ''}`} ref={videoContainerRef}>
      {/* UI elements will be created dynamically by createUIElements function */}
    </div>
  );
};

export const updateUiOnDemand = (senderUserId, type, typeValue, userId) => {
  console.group('🔔 updateUiOnDemand');
  console.log('Parameters:', { senderUserId, type, typeValue, userId });
  console.groupEnd();

  if (type === 'updateMuteUnmute') {
    const muteStatusElement = document.getElementById(`mute-status-${senderUserId}`);
    
    if (muteStatusElement) {
      muteStatusElement.src = typeValue ? mute_incall : unmute_incall;
      muteStatusElement.style.backgroundColor = typeValue ? '#FF1D1D' : '#16A249';
      
      if (typeValue) { 
        muteStatusElement.classList.add('mute');
      } else { 
        muteStatusElement.classList.remove('mute');
      }
    } else {
      console.warn(`⚠️ Mute status element not found for user: ${senderUserId}`);
    }
  } else if (type === 'updateRiseHand') {
    const isLocalUser = senderUserId === userId;
    console.log('Details:', { isLocalUser, senderUserId, userId, typeValue });

    const updateUserHandRaising = (targetUserId, handRaised) => {

      const risehandStatusElement = document.getElementById(`risehand-status-${targetUserId}`);
      
      if (risehandStatusElement) {
        risehandStatusElement.style.display = handRaised ? 'block' : 'none';
      } else {
        console.warn(`⚠️ Rise hand element not found: risehand-status-${targetUserId}`);
      }

      const videoContainer = risehandStatusElement?.parentElement;
      
      if (videoContainer) {
        const isRemoteUser = videoContainer.classList.contains('remoteUser');
        const isLocalUser = videoContainer.className.includes('local-video-container') ||
                           videoContainer.querySelector('.short-name') !== null;

        if (isRemoteUser || isLocalUser) {
          if (handRaised) {
            videoContainer.style.border = '2px solid #F25920';
            videoContainer.style.boxShadow = '0 0 0 2px #F25920';
          } else {
            videoContainer.style.border = '';
            videoContainer.style.boxShadow = '';
          }
        } else {
          console.warn('⚠️ Video container not identified as local or remote');
        }
      } else {
        console.warn(`⚠️ Video container not found for user ${targetUserId}`);
      }
      
      console.groupEnd();
    };

    if (isLocalUser) {
      updateUserHandRaising(userId, typeValue);
      return;
    }

    updateUserHandRaising(senderUserId, typeValue);

    // Fallback retry mechanism
    if (!document.getElementById(`risehand-status-${senderUserId}`)) {
      let retryCount = 0;
      const maxRetries = 5;
      const retryDelay = 100;

      const retryUpdate = () => {
        retryCount++;
        
        const retryElement = document.getElementById(`risehand-status-${senderUserId}`);
        if (retryElement) {
          updateUserHandRaising(senderUserId, typeValue);
        } else if (retryCount < maxRetries) {
          setTimeout(retryUpdate, retryDelay * retryCount);
        } else {
          console.error(`❌ Could not find element after ${maxRetries} retries`);
        }
      };

      setTimeout(retryUpdate, retryDelay);
    }
  } else {
    console.warn(`⚠️ Unknown update type: ${type}`);
  }
};

export let globalParticount = 1;

export const updateCount = (count) => {
  globalParticount = count;
};

export default React.memo(RenderDynamicView);